import { Component, OnInit } from '@angular/core';
import { UsersAdminService } from '../services/users-admin.service';
import { SelectedOrganisationService } from '../services/selected-organisation.service';
import { finalize, switchMap } from 'rxjs';
import { LaunchpadUser } from '../models/launchpad-user';
import { UserOrganisationsService } from '../services/user-organisations.service';
import { OrganisationTree } from '../models/organisation-tree';
import { OrganisationBase } from '../models/organisation-base';
import { NgbModal, NgbModalOptions } from '@ng-bootstrap/ng-bootstrap';
import { NewUserComponent } from '../new-user/new-user.component';
import { ExportRequest, RolesService, ToasterService, ToastLevel } from 'aih-launchpad-core';
import { TranslateService } from '@ngx-translate/core';
import { ErrorCheckService } from '../services/error-check.service';
import { ActivateUserModalComponent } from '../activate-user-modal/activate-user-modal.component';
import { DeleteUserModalComponent } from '../delete-user-modal/delete-user-modal.component';
import { ImpersonateUserModalComponent } from '../impersonate-user-modal/impersonate-user-modal.component';
import { Roles } from '../../constants/roles';

@Component({
    selector: 'app-user-list-wrapper',
    templateUrl: './user-list-wrapper.component.html',
    styleUrls: ['./user-list-wrapper.component.sass'],
    standalone: false
})
export class UserListWrapperComponent implements OnInit {
  protected users: LaunchpadUser[] = [];
  protected loading: boolean = true;
  protected authError: boolean = false;

  protected organisationTree: OrganisationTree | null = null;
  protected selectedOrgs: OrganisationBase[] | null = null;

  protected hasImpersonatorRole: boolean = false;
  protected readOnly: boolean = false;

  modalOptions: NgbModalOptions = {
    modalDialogClass: 'modal-dialog',
    container: 'body',
    centered: true,
    backdrop: 'static',
  };

  protected exporting: boolean = false;

  searchTerm = '';
  onlyActive: boolean = false;
  onlyImpersonationRequested: boolean = false;

  constructor(
    private userAdminService: UsersAdminService,
    private selectedOrgService: SelectedOrganisationService,
    private userOrganisationsService: UserOrganisationsService,
    private modalService: NgbModal,
    private toasterService: ToasterService,
    private translateService: TranslateService,
    private errorCheckService: ErrorCheckService,
    private rolesService: RolesService
  ) {}

  ngOnInit(): void {
    this.rolesService.hasAnyRoles([Roles.Impersonator]).then(isImpersonator => {
      this.hasImpersonatorRole = isImpersonator;
      this.initiateUserSubscription();
    })

    // Users that only have the Reader role may view user details, but not change anything
    Promise.all([
      this.rolesService.hasAnyRoles([Roles.Admin]),
      this.rolesService.hasAnyRoles([Roles.Reader])
    ]).then(([isAdmin, isReader]) => {
      this.readOnly = !isAdmin && isReader;
    });

    this.userOrganisationsService.getUserOrganisations().subscribe(
      (organisationTree) => {
        this.organisationTree = organisationTree;
      },
      (err) => {
        this.authError = this.errorCheckService.isErrorUnauthorised(err);
        this.loading = false;
      }
    );
  }

  public onSearchTermProvided(searchTerm: string): void {
    this.searchTerm = searchTerm;
    this.refetchUsersForOrganisation();
  }

  /**
   * Downloads the users of the selected organisations. A filtered export also applies the search term
   * and filter toggles, so it holds exactly what the table shows.
   */
  public exportUsers({ format, filtered }: ExportRequest): void {
    this.exporting = true;

    this.userAdminService
      .getUsersExport(
        format,
        this.translateService.currentLang,
        this.selectedOrgs?.map((o) => o.organisationRef) ?? [],
        this.hasImpersonatorRole,
        filtered ? this.searchTerm : '',
        filtered && this.onlyActive,
        filtered && this.onlyImpersonationRequested
      )
      .pipe(finalize(() => (this.exporting = false)))
      .subscribe({
        next: (file) => this.userAdminService.downloadExportFile(file),
        error: (err) => {
          this.toasterService.show({
            header: this.translateService.instant('admin.export.errorToast.title'),
            content: this.translateService.instant('admin.export.errorToast.message'),
            level: ToastLevel.Error,
          });

          console.error('Unable to export the users', err);
        },
      });
  }

  public openNewUserModal(): Promise<void> {
    let modalOptions = {
      ...this.modalOptions,
      size: 'xl',
    };
    const modalRef = this.modalService.open(NewUserComponent, modalOptions);
    (modalRef.componentInstance as NewUserComponent).organisationTree =
      this.organisationTree;

    modalRef.result.then(
      (newUser) => {
        this.refetchUsersForOrganisation();
        this.toasterService.show({
          header: this.translateService.instant('admin.userCreated'),
          content: this.translateService.instant('admin.reviewDetails'),
          level: ToastLevel.Success,
        });
      },
      (reason) => {}
    );

    return modalRef.result;
  }

  public openEditUserModal(user: LaunchpadUser, readOnly: boolean = false): Promise<void> {
    let modalOptions = {
      ...this.modalOptions,
      size: 'xl',
    };
    const modalRef = this.modalService.open(NewUserComponent, modalOptions);
    (modalRef.componentInstance as NewUserComponent).organisationTree =
      this.organisationTree;
    (modalRef.componentInstance as NewUserComponent).readOnly = readOnly;
    (modalRef.componentInstance as NewUserComponent).selectedUser = user;

    modalRef.result.then(
      (updatedUser) => {
        this.refetchUsersForOrganisation();
        this.toasterService.show({
          header: this.translateService.instant('admin.userUpdated'),
          content: this.translateService.instant('admin.reviewDetails'),
          level: ToastLevel.Success,
        });
      },
      (reason) => {}
    );

    return modalRef.result;
  }

  public openActivationUserModal(user: LaunchpadUser): Promise<void> {
    let modalOptions = {
      ...this.modalOptions,
      size: 'md',
    };
    const modalRef = this.modalService.open(
      ActivateUserModalComponent,
      modalOptions
    );
    const activateUserModalComponent =
      modalRef.componentInstance as ActivateUserModalComponent;
    activateUserModalComponent.user = user;

    modalRef.result.then(
      (updatedUser) => {
        this.refetchUsersForOrganisation();
        if (updatedUser.isActive) {
          // user has been reactivated
          this.toasterService.show({
            header: this.translateService.instant(
              'admin.reactivateUser.toast.title'
            ),
            content: this.translateService.instant(
              'admin.reactivateUser.toast.message'
            ),
            level: ToastLevel.Success,
          });
        } else {
          // user has been deactivated
          this.toasterService.show({
            header: this.translateService.instant(
              'admin.deactivateUser.toast.title'
            ),
            content: this.translateService.instant(
              'admin.deactivateUser.toast.message'
            ),
            level: ToastLevel.Success,
          });
        }
      },
      (reason) => {}
    );

    return modalRef.result;
  }

  public openDeleteUserModal(user: LaunchpadUser): Promise<void> {
    let modalOptions = {
      ...this.modalOptions,
      size: 'md',
    };
    const modalRef = this.modalService.open(
      DeleteUserModalComponent,
      modalOptions
    );
    (modalRef.componentInstance as DeleteUserModalComponent).user = user;

    modalRef.result.then(
      () => {
        this.refetchUsersForOrganisation();
        this.toasterService.show({
          header: this.translateService.instant('admin.deleteUser.toast.title'),
          content: this.translateService.instant('admin.deleteUser.toast.message'),
          level: ToastLevel.Success,
        });
      },
      (reason) => {
        // A deletion that failed part way leaves the user pending deletion, so show that in the list
        if (reason === DeleteUserModalComponent.DeletionFailedReason) {
          this.refetchUsersForOrganisation();
        }
      }
    );

    return modalRef.result;
  }

  public resendUserInvite(user: LaunchpadUser) {
    this.userAdminService.resendUserInvite(user.emailAddress).subscribe(
      () =>
        this.toasterService.show({
          header: this.translateService.instant(
            'admin.resendInvite.successToast.title'
          ),
          content: this.translateService.instant(
            'admin.resendInvite.successToast.message'
          ),
          level: ToastLevel.Success,
        }),
      (err) => {
        this.toasterService.show({
          header: this.translateService.instant(
            'admin.resendInvite.errorToast.title'
          ),
          content: this.translateService.instant(
            'admin.resendInvite.errorToast.message'
          ),
          level: ToastLevel.Error,
        });

        console.error(`Unable to resend user invite due to - ${err}`);
      }
    );
  }

  public openImpersonationModal(user: LaunchpadUser): void {
    let modalOptions = {
      ...this.modalOptions,
      size: 'md',
    };
    const modalRef = this.modalService.open(
      ImpersonateUserModalComponent,
      modalOptions
    );
    const modalComponent = modalRef.componentInstance as ImpersonateUserModalComponent;
    modalComponent.user = user;

    // No logic after modal closure is required
    // as the confirmation will trigger the log-out and redirect
  }

  onOrganisationSelected(organisation: OrganisationBase | null): void {
    this.selectedOrgService.selectOrganisation(organisation);
  }

  private initiateUserSubscription(): void {
    this.selectedOrgService.selectedOrgs$
      .pipe(
        switchMap((selectedOrgs) => {
          this.selectedOrgs = selectedOrgs;

          this.loading = true;
          return this.userAdminService.getUsersForOrganisation(
            selectedOrgs?.map((o) => o.organisationRef) ?? [],
            this.hasImpersonatorRole,
            this.searchTerm,
            this.onlyActive,
            this.onlyImpersonationRequested
          );
        })
      )
      .subscribe({
        next: users => {
          this.users = users;
          this.loading = false;
        },
        error: ({ networkError }) => {
          this.authError =
            this.errorCheckService.isErrorUnauthorised(networkError);
          this.loading = false;
        }}
      );
  }

  protected refetchUsersForOrganisation(): void {
    this.loading = true;
    this.userAdminService
      .getUsersForOrganisation(
        this.selectedOrgs?.map((o) => o.organisationRef) ?? [],
        this.hasImpersonatorRole,
        this.searchTerm,
        this.onlyActive,
        this.onlyImpersonationRequested
      )
      .subscribe((users) => {
        this.users = users;
        this.loading = false;
      });
  }
}
